import React, { useEffect, useState, useRef } from 'react';
import { Container, Paper, Typography, Box, Button, CircularProgress, Alert } from '@mui/material';
import { Error } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { orderService } from '../services/api';
import { useCart } from '../contexts/CartContext';
import { toast } from 'react-toastify';

const PaymentFailure = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { restoreCart } = useCart();
  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState(null);
  const [cartRestored, setCartRestored] = useState(false);
  const restoreAttemptedRef = useRef(false);

  useEffect(() => {
    const handlePaymentFailure = async () => {
      if (restoreAttemptedRef.current) {
        // Prevent double invocation (React StrictMode or remounts)
        return;
      }
      restoreAttemptedRef.current = true;
      try {
        // Log all URL parameters received from PayU
        //console.log('=== PaymentFailure Page Loaded ===');
        //console.log('Current URL:', window.location.href);
        //console.log('Search Params String:', searchParams.toString());
        //console.log('All URL Parameters:');
        for (const [key, value] of searchParams.entries()) {
          //console.log(`  ${key}: ${value}`);
        }
        //console.log('===================================');
        
  const orderId = searchParams.get('udf1');
        const txnid = searchParams.get('txnid');
        
        //console.log('Extracted PayU data:', { orderId, txnid });
        
        if (orderId) {
          if(orderId.startsWith('ORD')){
            //console.log('Fetching order details for failed payment...');
            const response = await orderService.getOrder(orderId);
            const order = response.data.order;
            setOrderData(order);
            if (order && order.items && !cartRestored) {
              const cartData = {
                items: order.items,
                couponCode: order.couponCode,
                discount: order.discount,
                total: order.pricing?.total || undefined
              };
              await restoreCart(cartData);
              setCartRestored(true);
            }
            await orderService.updateOrderStatus(orderId, 'cancelled', 'failed', 'Payment failed');
          } else if(orderId.startsWith('SES')) {
            // Session never converted to order; cart already has items; nothing to cancel
            toast.info('Payment failed. Your cart items are intact.');
          }
        }
      } catch (error) {
        console.error('Error handling payment failure:', error);
        toast.error('Failed to restore cart from failed payment');
      } finally {
        setLoading(false);
      }
    };

    handlePaymentFailure();
  }, [searchParams, restoreCart]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Processing payment failure...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Error sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Payment Failed
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Unfortunately, your payment could not be processed
        </Typography>
        
        {cartRestored && (
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            Don't worry! Your cart has been restored and you can try the payment again.
          </Alert>
        )}
        
        <Typography variant="body1" sx={{ mt: 2, mb: 4 }}>
          You can try again with the same payment method or choose a different one.
          Your items are still in your cart.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            onClick={() => navigate('/checkout')}
            disabled={!cartRestored && !orderData}
          >
            Try Payment Again
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/cart')}
          >
            Review Cart
          </Button>
          <Button 
            variant="text" 
            onClick={() => navigate('/')}
          >
            Continue Shopping
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default PaymentFailure;
